import moment from 'moment';

import { NewDatePickerProps } from '../../../NewDatePicker/NewDatePicker';

import { CustomDate } from '../Calendar/CalendarShortcuts/CalendarShortcuts';
import { DateMask } from '../Calendar/Calendar';

import { FULL_DATE, SHORT_DATE } from '../Calendar/Constants';
import { formatInkDate, normalizeDate, LOCALES } from '../../../../helpers/datetime';

export const getActualDateValue = (value: string, format?: NewDatePickerProps['format']): Date | string => {
  // TODO: move this into extractDateTimeInfo in a future PR
  const { dateParts } = normalizeDate({ value });

  if (!format) return value;

  return (dateParts && new Date(dateParts.year, dateParts.month, dateParts.day)) || value;
};

export const getMomentDate = (date: CustomDate, dateMask: DateMask) => {
  const momentDate = moment(date, dateMask);
  return momentDate.isValid() ? momentDate : moment(new Date(), dateMask);
};

interface InitialValueType {
  value: NewDatePickerProps['value'];
  format?: NewDatePickerProps['format'];
  dateMask?: typeof FULL_DATE | typeof SHORT_DATE;
  locale?: NewDatePickerProps['locale'];
}

// this one is used to get the initial input from user and format it in the specific expected
// for NewDatePicker's internal input
export const defineInputInitialValue = ({ value, format, locale = LOCALES.EN_US }: InitialValueType): string => {
  if (format) {
    // TODO: move this into extractDateTimeInfo in a future PR
    return formatInkDate({ value, format, locale }).formatted;
  }

  return value || '';
};

// this one is used to get initial input from user and normalize the date to be used internally by NewDatePicker
export const defineInternalInitialValue = ({ value, format, dateMask, locale = LOCALES.EN_US }: InitialValueType) => {
  if (format) {
    return getActualDateValue(value, format);
  }
  return value || moment().format(dateMask);
};

export const defineInitialSuggestedMonth = (value: CustomDate, dateMask: DateMask) => {
  if (value && dateMask === 'MM/YYYY') {
    return moment(value, dateMask).get('month');
  }
  return dateMask === 'MM/YYYY' ? moment().get('month') : undefined;
};

export const defineInitialSuggestedYear = (value: CustomDate, dateMask: DateMask) => {
  if (value && dateMask === 'MM/YYYY') {
    return moment(value, dateMask).get('year');
  }
  return dateMask === 'MM/YYYY' ? moment().get('year') : undefined;
};

export const getLocalizedDay = (date: Date, locale: string) => {
  return date.toLocaleString(locale, { weekday: 'short' });
};

export const getLocalizedMonth = (month: number, locale: string) => {
  const objDate = new Date();
  objDate.setDate(1);
  objDate.setMonth(month);

  return objDate.toLocaleString(locale, { month: 'long' });
};

export const getLocalizedMonths = (locale: string) => {
  const monthsRange = [...Array(12).keys()];
  return monthsRange.map(month => getLocalizedMonth(month, locale));
};

export const getMask = (
  mask: NewDatePickerProps['mask'],
  format: NewDatePickerProps['format'],
  locale: NewDatePickerProps['locale'] = LOCALES.EN_US,
) => {
  if (format && !!mask) {
    throw new Error(`[_ink] format "${format}" should not be used with mask.`);
  }

  // Restrict `mask` to en-US only
  if ((mask && locale === LOCALES.EN_GB) || (mask && typeof locale === 'object' && locale.output === LOCALES.EN_GB)) {
    throw new Error(
      `[NewDatePicker] Masking is only supported for ${LOCALES.EN_US} locale. Remove the mask or switch to ${LOCALES.EN_GB}.`,
    );
  }
  // Ensure backward compatibility by defaulting to 'date' when no format or mask is provided
  return format ? undefined : mask || 'date';
};
